import {fabric} from 'fabric';
import {useCallback, useContext, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {canvasHideTextBorder} from '~/common/canvas/customs/text_border';
import {deriveIdFromCanvasObject} from '~/common/canvas/utils/canvas';
import {UploadButton} from '~/common/FabricInvitation/EventCreateDesign/components/CardEditor/actions/UploadButton/UploadButton';
import {EventCreateContext} from '~/common/FabricInvitation/EventCreateDesign/EventCreateContext';
import {
  UPDATE_DESIGN_OPTIONS,
  UPDATE_PRISTINE_UYO,
} from '~/common/fabric/reducers/fabricDesign/constants';
import {selectPristineUyo} from '~/common/fabric/reducers/fabricDesign/selectors';
import {useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {useShadowImage} from './hooks/useShadowImage';

import './Uyo.sass';

export const Uyo = ({btnText = '', nodeRef, index, id, btnSize = 'large'}) => {
  const {canvas} = useContext(EventCreateContext);
  const [styles, setStyles] = useState({});
  const pristineUyo = useSelector(selectPristineUyo);
  const isDesktop = useIsLargishAndUp();

  useShadowImage(canvas);

  let uyoImage;
  const dispatch = useDispatch();
  const selectUyo = useCallback(
    (e) => {
      uyoImage = canvas
        .getObjects()
        .find((obj) => obj.type === 'uYOImage' && (obj.id || deriveIdFromCanvasObject(obj)) === id);
      const rect = canvas
        .getObjects()
        .find((obj) => obj.type === 'rect' && (obj.id || deriveIdFromCanvasObject(obj)) === id);
      const activeTextboxes = canvas.getActiveObjects().filter((obj) => obj.type === 'textbox');
      const mousePosition = canvas.getPointer(e);
      const texts = canvas.getObjects().filter(({type}) => type === 'textbox');
      const pointer = canvas.getPointer(e, true);
      const target = canvas._searchPossibleTargets(texts, pointer);
      if (target) {
        canvas.setActiveObject(target).renderAll();
        canvasHideTextBorder(target, canvas);
      } else if (
        uyoImage &&
        uyoImage.selectable !== false &&
        rect &&
        rect.containsPoint(mousePosition, null, true) &&
        activeTextboxes.length === 0
      ) {
        canvas.setActiveObject(uyoImage).renderAll();
      }
    },
    [canvas, id, uyoImage]
  );

  const positionBtn = useCallback(
    (obj) => {
      if (!obj || typeof obj.getCenterPoint !== 'function') return;
      // for some reason, this needs to be delayed or the upload button will not be centered
      setTimeout(() => {
        const {x: left, y: top} = obj.getCenterPoint();
        const zoom = isDesktop ? 0.5 : 0.245;

        setStyles({
          left: `${left * zoom}px`,
          top: `${top * zoom - (obj.type === 'uYOImage' && obj.variant === 'logo' ? 16 : 20)}px`,
          display: 'flex',
        });
      }, 0);
    },
    [isDesktop]
  );

  const overrideFindTarget = useCallback(() => {
    fabric.util.addListener(canvas.upperCanvasEl, 'mousedown', selectUyo);
    fabric.util.addListener(canvas.upperCanvasEl, 'touchstart', selectUyo);

    canvas.findTarget = (e, skipGroup) => {
      if (canvas.skipTargetFind) {
        return undefined;
      }
      const ignoreZoom = true;
      const pointer = canvas.getPointer(e, ignoreZoom);
      const activeObject = canvas._activeObject;
      const aObjects = canvas.getActiveObjects();
      let activeTarget;
      let activeTargetSubs;
      const isTouch = fabric.util.isTouchEvent(e);
      const shouldLookForActive = (aObjects.length > 1 && !skipGroup) || aObjects.length === 1;

      canvas.targets = [];

      if (shouldLookForActive && activeObject._findTargetCorner(pointer, isTouch)) {
        return activeObject;
      }

      if (
        aObjects.length > 1 &&
        !skipGroup &&
        activeObject === canvas._searchPossibleTargets([activeObject], pointer)
      ) {
        return activeObject;
      }
      if (aObjects.length === 1 && !!canvas._searchPossibleTargets([activeObject], pointer)) {
        return activeObject;
      }
      let target = canvas._searchPossibleTargets(canvas._objects, pointer);
      if (e[canvas.altSelectionKey] && target && activeTarget && target !== activeTarget) {
        target = activeTarget;
        canvas.targets = activeTargetSubs;
      }
      return target;
    };
  }, [canvas, selectUyo]);

  const imageUploadedCallback = useCallback(
    (image) => {
      overrideFindTarget();
      dispatch({
        type: UPDATE_PRISTINE_UYO,
        payload: {pristineUyo: false, id},
      });
      image.setControlsVisibility({
        mtr: false,
        remove: false,
      });
      canvas.setActiveObject(image);
    },
    [overrideFindTarget, dispatch, id, canvas]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    if (canvas) {
      uyoImage = canvas
        .getObjects()
        .filter((obj) => obj.type === 'uYOImage')
        .filter((obj) => obj.id)
        .find((obj) => deriveIdFromCanvasObject(obj) === id);
      uyoImage?.setControlsVisibility?.({
        mtr: false,
        remove: false,
      });
      const rect = canvas
        .getObjects()
        .find(
          (obj) =>
            obj.type === 'rect' && (obj.id || deriveIdFromCanvasObject(obj)) === `${id}-container`
        );
      const textbox = canvas.getObjects().filter((obj) => obj.type === 'textbox');

      const positionBtnHandler = () => positionBtn(uyoImage ?? rect, canvas);
      const resizeObserver = new ResizeObserver(positionBtnHandler);
      positionBtnHandler();
      if ((!rect && uyoImage) || uyoImage?.isError) {
        resizeObserver.observe(canvas.wrapperEl);
        dispatch({
          type: UPDATE_PRISTINE_UYO,
          payload: {pristineUyo: true, id},
        });
      } else if (rect && uyoImage) {
        uyoImage.hasControls = true;
        // TODO: Do we need this?
        // uyoImage._originalObject = rect.toJSON();
        overrideFindTarget();
      }

      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {hasTextbox: !!textbox.length},
      });

      return () => {
        resizeObserver.disconnect();
      };
    }
    return undefined;
  }, [!!canvas, canvas?.getObjects()?.length, id]);

  const showUyoButton = pristineUyo[id] === true;

  return showUyoButton ? (
    <UploadButton
      btnText={btnText}
      btnSize={btnSize}
      styles={styles}
      className="uyo__action__button"
      callback={imageUploadedCallback}
      ref={nodeRef}
      id={id}
      index={index}
    />
  ) : null;
};
